// Versiyon: 1.0.0 - Sipariş Listesi Sayfası
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  ButtonGroup,
  CircularProgress,
  Alert,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import {
  ORDER_FILTERS_EVENT,
  consumePendingOrderFilters,
  type OrderFilterRequest,
} from '../../aichat/agent/uiBridge';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import AccountingDataTable from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PendingIcon from '@mui/icons-material/Pending';
import WaybillDisplayModal from '../waybills/WaybillDisplayModal';
import type { WaybillData } from '../waybills/WaybillDisplayModal';
import InvoiceDisplayModal from '../invoice/InvoiceDisplayModal';
import type { InvoiceData } from '../invoice/InvoiceDisplayModal';
import OrderDisplayModal from './OrderDisplayModal';
import type { OrderData } from './OrderDisplayModal';
import { useTranslation } from 'react-i18next';
import { getIncomingPurchaseOrders } from '../../../services/ecommerceApi';
import { getBranches } from '../../../services/migration/branchDataService';
import IncomingOrderDetailDialog, { statusChip as b2bStatusChip } from '../intercompany/IncomingOrderDetailDialog';
import HubIcon from '@mui/icons-material/Hub';

// Sipariş listesi arayüzü
interface OrderListItem {
  id: string;
  orderNumber: string;
  orderType: string; // 'SALES_ORDER' | 'PURCHASE_ORDER'
  type: 'sales' | 'purchase'; // UI için
  accountName: string;
  /** PYS: bağlı olduğu projenin adı (bağ yoksa null). */
  projectName?: string | null;
  orderDate: string;
  deliveryDate: string | null;
  grandTotal: number;
  currency: string;
  status: string;
  processStock: boolean;
  isDeliveryOverdue: boolean;
  convertedInvoiceId: string | null;
  linkedInvoiceNumber: string | null;
  sourceOfferId: string | null;
  sourceOfferNumber: string | null;
  linkedWaybillId: string | null;
  linkedWaybillNumber: string | null;
  createdAt: string;
  /** Kaynak modül: undefined/'accounting' → cari sipariş, 'b2b' → gelen alış siparişi. */
  source?: 'accounting' | 'b2b';
  /** B2B: satıcı firmanın verdiği sipariş bu tenant'a aynalandı. */
  b2bInvoiced?: boolean;
  b2bUnmatchedCount?: number;
  b2bItemCount?: number;
  b2bBranchId?: string | null;
}

const Orders: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  // Sipariş türü filtresi
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'sales' | 'purchase' | 'b2b'>('all');
  
  // Filtreleri göster/gizle
  const [showFilters, setShowFilters] = useState(false);
  
  // 3 nokta menüsü
  const [anchorElFeatures, setAnchorElFeatures] = useState<null | HTMLElement>(null);
  
  // Sıralama
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  
  // Arama
  const [searchTerm, setSearchTerm] = useState('');
  
  // Durum filtresi
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Açılır menü
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderType, setSelectedOrderType] = useState<'sales' | 'purchase'>('sales');
  
  // Veri durumu
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Sayfalama
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Silme onay dialogu
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  
  // Faturaya dönüştürme dialogu
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [orderToConvert, setOrderToConvert] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  
  // Excel loading
  const [loadingExport, setLoadingExport] = useState(false);
  
  // İrsaliye görüntüleme modalı
  const [isWaybillModalOpen, setIsWaybillModalOpen] = useState(false);
  const [selectedWaybillForModal, setSelectedWaybillForModal] = useState<WaybillData | null>(null);
  
  // Fatura görüntüleme modalı
  const [isInvoiceDisplayModalOpen, setIsInvoiceDisplayModalOpen] = useState(false);
  const [selectedInvoiceForDisplay, setSelectedInvoiceForDisplay] = useState<InvoiceData | null>(null);
  
  // Sipariş görüntüleme modal state'leri
  const [isOrderDisplayModalOpen, setIsOrderDisplayModalOpen] = useState(false);
  const [selectedOrderForDisplay, setSelectedOrderForDisplay] = useState<OrderData | null>(null);
  const [loadingOrderDisplay, setLoadingOrderDisplay] = useState(false);
  
  // 3 nokta menüsü handler'ları
  const handleOpenFeaturesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFeatures(event.currentTarget);
  };
  const handleCloseFeaturesMenu = () => {
    setAnchorElFeatures(null);
  };
  
  // Sayfa değişimi
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sıralama değişikliği
  const handleSortChange = (column?: string) => {
    if (column && column !== sortColumn) {
      setSortColumn(column);
      setSortDirection('desc');
    } else {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };
  
  // Menü handler'ları
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string, type: 'sales' | 'purchase') => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderId(id);
    setSelectedOrderType(type);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrderId(null);
  };

  // Durum rengi
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const lowerStatus = status?.toLowerCase() || '';
    switch (lowerStatus) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'draft': return 'default';
      case 'shipped': return 'info';
      case 'delivered': return 'success';
      case 'canceled': return 'error';
      case 'invoiced': return 'info';
      default: return 'default';
    }
  };

  // Durum Türkçe çevirisi
  const getStatusText = (status: string): string => {
    const lowerStatus = status?.toLowerCase() || '';
    switch (lowerStatus) {
      case 'draft': return tt('Taslak', 'Draft');
      case 'pending': return tt('Beklemede', 'Pending');
      case 'confirmed': return tt('Onaylandı', 'Confirmed');
      case 'shipped': return tt('Sevk Edildi', 'Shipped');
      case 'delivered': return tt('Teslim Edildi', 'Delivered');
      case 'canceled': return tt('İptal Edildi', 'Canceled');
      case 'invoiced': return tt('Faturalandırıldı', 'Invoiced');
      default: return status;
    }
  };

  // Tarihi DD.MM.YYYY formatına dönüştürme
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString(localeCode, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Para formatı
  const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
    return new Intl.NumberFormat(localeCode, { style: 'currency', currency }).format(amount);
  };

  // Şirket ID'sini yükleme
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError(tt('Kullanıcı oturumu bulunamadı.', 'User session not found.'));
          setLoading(false);
          return;
        }
        const userData = await firebaseDataService.getUserData(uid);
        if (userData && userData.companyId) {
          setCompanyId(userData.companyId);
        } else {
          setError(tt('Kullanıcı bilgileri bulunamadı.', 'User information not found.'));
          setLoading(false);
        }
      } catch (err) {
        console.error('Veri yükleme hatası:', err);
        setError(tt('Veriler yüklenirken bir hata oluştu.', 'An error occurred while loading data.'));
        setLoading(false);
      }
    };
    loadInitialData();
  }, []);
  
  // Server-side sayfalama ile siparişleri yükleme
  const loadOrders = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const orderTypeParam = orderTypeFilter === 'all' ? undefined : (orderTypeFilter === 'sales' ? 'SALES_ORDER' : 'PURCHASE_ORDER');
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined;
      
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_COMPANY_PAGED({
        page: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        orderType: orderTypeParam,
        status: statusParam,
      }));
      
      const result = response.data;
      if (!result) {
        console.warn('⚠️ [SİPARİŞ] API boş yanıt döndü');
        setOrders([]);
        setTotalCount(0);
        return;
      }
      
      if (result.success && result.data) {
        const ordersList: OrderListItem[] = result.data.map((order: any) => {
          const isSales = order.orderType === 'SALES_ORDER';
          return {
            id: order.id,
            orderNumber: order.orderNumber || '',
            orderType: order.orderType || '',
            type: isSales ? 'sales' as const : 'purchase' as const,
            accountName: order.accountName || (isSales ? tt('İsimsiz Müşteri', 'Unnamed Customer') : tt('İsimsiz Tedarikçi', 'Unnamed Supplier')),
            projectName: order.projectName || null,
            orderDate: order.orderDate ? order.orderDate.split('T')[0] : '',
            deliveryDate: order.deliveryDate ? order.deliveryDate.split('T')[0] : null,
            grandTotal: order.grandTotal || 0,
            currency: order.currency || 'TRY',
            status: order.status || 'draft',
            processStock: order.processStock || false,
            isDeliveryOverdue: order.isDeliveryOverdue || false,
            convertedInvoiceId: order.convertedInvoiceId || null,
            linkedInvoiceNumber: order.linkedInvoiceNumber || null,
            sourceOfferId: order.sourceOfferId || null,
            sourceOfferNumber: order.sourceOfferNumber || null,
            linkedWaybillId: order.linkedWaybillId || null,
            linkedWaybillNumber: order.linkedWaybillNumber || null,
            createdAt: order.createdAt || '',
          };
        });
        // ✅ Detaylı dönüşüm logları
        console.log('📋 [SİPARİŞ] Toplam sipariş:', ordersList.length);
        console.log('📋 [SİPARİŞ] Dönüşüm alanları:', ordersList.map(o => ({
          id: o.id,
          orderNumber: o.orderNumber,
          status: o.status,
          convertedInvoiceId: o.convertedInvoiceId,
          linkedInvoiceNumber: o.linkedInvoiceNumber,
          linkedWaybillId: o.linkedWaybillId,
          linkedWaybillNumber: o.linkedWaybillNumber,
        })));
        console.log('📋 [SİPARİŞ] API ham yanıtı (ilk 3):', result.data.slice(0, 3).map((o: any) => ({
          id: o.id,
          orderNumber: o.orderNumber,
          convertedInvoiceId: o.convertedInvoiceId,
          linkedInvoiceId: o.linkedInvoiceId,
          linkedInvoiceNumber: o.linkedInvoiceNumber,
          linkedWaybillId: o.linkedWaybillId,
          linkedWaybillNumber: o.linkedWaybillNumber,
          status: o.status,
        })));
        setOrders(ordersList);
        setTotalCount(result.totalCount || 0);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('❌ Siparişler yüklenirken hata:', err);
      setError(err.message || tt('Siparişler yüklenirken bir hata oluştu.', 'An error occurred while loading orders.'));
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, searchTerm, sortColumn, sortDirection, orderTypeFilter, statusFilter, isTr]);

  // Server-side yükleme - debounce ile
  useEffect(() => {
    if (!companyId) return;
    const timer = setTimeout(() => {
      loadOrders();
    }, 400);
    return () => clearTimeout(timer);
  }, [loadOrders]);

  // ---------------- B2B gelen alış siparişleri (görsel birleştirme) ----------------
  // Ayrı veri kaynağı (IncomingPurchaseOrders); Cari listeye "B2B" satırları olarak katılır.
  const [b2bOrders, setB2bOrders] = useState<OrderListItem[]>([]);
  const [b2bBranchMap, setB2bBranchMap] = useState<Record<string, string>>({});
  const [b2bDetailOrderId, setB2bDetailOrderId] = useState<string | null>(null);
  const [b2bDetailOpen, setB2bDetailOpen] = useState(false);

  const loadB2bOrders = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await getIncomingPurchaseOrders({ pageSize: 100 });
      const rows: OrderListItem[] = (res.data || []).map((o) => ({
        id: o.id,
        orderNumber: o.sellerOrderNumber || '-',
        orderType: 'B2B',
        type: 'purchase' as const,
        accountName: o.sellerCompanyName || tt('B2B Tedarikçi', 'B2B Supplier'),
        projectName: null,
        orderDate: o.orderedAt ? o.orderedAt.split('T')[0] : '',
        deliveryDate: null,
        grandTotal: o.totalAmount || 0,
        currency: o.currency || 'TRY',
        status: o.status || 'pending',
        processStock: false,
        isDeliveryOverdue: false,
        convertedInvoiceId: o.invoiceId || null,
        linkedInvoiceNumber: null,
        sourceOfferId: null,
        sourceOfferNumber: null,
        linkedWaybillId: null,
        linkedWaybillNumber: null,
        createdAt: o.orderedAt || '',
        source: 'b2b' as const,
        b2bInvoiced: o.isInvoiced,
        b2bUnmatchedCount: o.unmatchedCount,
        b2bItemCount: o.itemCount,
        b2bBranchId: o.branchId || null,
      }));
      setB2bOrders(rows);
    } catch (err) {
      // B2B ucu erişilemezse cari liste normal çalışmaya devam eder.
      console.warn('[SİPARİŞ] B2B gelen siparişler yüklenemedi:', err);
      setB2bOrders([]);
    }
  }, [companyId, isTr]);

  useEffect(() => {
    loadB2bOrders();
  }, [loadB2bOrders]);

  // Şube adları (B2B detay başlığında göstermek için)
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const branches = await getBranches(companyId);
        if (cancelled) return;
        const map: Record<string, string> = {};
        branches.forEach((b) => { map[b.id] = b.name || tt('İsimsiz Şube', 'Unnamed Branch'); });
        setB2bBranchMap(map);
      } catch {
        /* şube listesi yüklenemezse başlıkta id gösterilir */
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, isTr]);

  const b2bBranchLabel = useCallback(
    (branchId?: string | null) => (branchId ? b2bBranchMap[branchId] || branchId : tt('Merkez', 'HQ')),
    [b2bBranchMap, isTr]
  );

  const openB2bDetail = (orderId: string) => {
    setB2bDetailOrderId(orderId);
    setB2bDetailOpen(true);
  };

  // Arama uygulanmış B2B satırları (firma adı / sipariş no)
  const b2bVisible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return b2bOrders;
    return b2bOrders.filter(
      (r) => r.accountName.toLowerCase().includes(term) || r.orderNumber.toLowerCase().includes(term)
    );
  }, [b2bOrders, searchTerm]);

  // Tabloya verilecek satırlar + toplam sayaç (cari server-side + B2B görsel katman)
  const { displayRows, displayTotalCount } = useMemo(() => {
    // Yalnız B2B: istemci tarafı sayfalama
    if (orderTypeFilter === 'b2b') {
      const start = page * rowsPerPage;
      return {
        displayRows: b2bVisible.slice(start, start + rowsPerPage),
        displayTotalCount: b2bVisible.length,
      };
    }
    // Tümü: B2B satırları ilk sayfada cari siparişlerle tarihe göre karışık gösterilir
    if (orderTypeFilter === 'all' && page === 0 && b2bVisible.length > 0) {
      const combined = [...b2bVisible, ...orders].sort((a, b) => {
        const da = new Date(a.orderDate || a.createdAt).getTime();
        const db = new Date(b.orderDate || b.createdAt).getTime();
        return db - da;
      });
      return { displayRows: combined, displayTotalCount: totalCount };
    }
    // Satış/Alış veya sonraki sayfalar: yalnız cari siparişler
    return { displayRows: orders, displayTotalCount: totalCount };
  }, [orderTypeFilter, page, rowsPerPage, b2bVisible, orders, totalCount]);

  // İstatistik kartları
  const orderStats = useMemo(() => {
    const pendingCount = orders.filter(o => o.status === 'pending' || o.status === 'draft').length;
    const pendingTotal = orders.filter(o => o.status === 'pending' || o.status === 'draft').reduce((sum, o) => sum + o.grandTotal, 0);
    
    const confirmedCount = orders.filter(o => o.status === 'confirmed' || o.status === 'shipped').length;
    const confirmedTotal = orders.filter(o => o.status === 'confirmed' || o.status === 'shipped').reduce((sum, o) => sum + o.grandTotal, 0);
    
    const overdueCount = orders.filter(o => o.isDeliveryOverdue).length;
    
    const totalExcludeDraft = orders.filter(o => o.status !== 'draft').length;
    const invoicedCount = orders.filter(o => o.status === 'invoiced').length;
    const conversionRate = totalExcludeDraft > 0 ? Math.round((invoicedCount / totalExcludeDraft) * 100) : 0;
    
    return { pendingCount, pendingTotal, confirmedCount, confirmedTotal, overdueCount, conversionRate };
  }, [orders]);

  // Arama/filtre yapıldığında sayfayı sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, orderTypeFilter, statusFilter, sortDirection, sortColumn]);

  // AI ajanı: sayfa filtrelerini doldur
  useEffect(() => {
    const applyAgentFilters = (req: OrderFilterRequest) => {
      if (!req) return;
      if (req.clearAll) {
        setSearchTerm('');
        setOrderTypeFilter('all');
        setStatusFilter('all');
      }
      if (req.searchText !== undefined) setSearchTerm(req.searchText);
      if (req.orderType !== undefined) {
        const t = String(req.orderType).toLowerCase();
        if (t === 'all' || t === '') setOrderTypeFilter('all');
        else if (t === 'sales' || t === 'purchase') setOrderTypeFilter(t);
      }
      if (req.status !== undefined) {
        const s = String(req.status).toLowerCase();
        setStatusFilter(s === '' ? 'all' : s);
      }
      setPage(0);
      setShowFilters(true);
    };
    const pending = consumePendingOrderFilters();
    if (pending) applyAgentFilters(pending);
    const handler = (e: Event) => {
      consumePendingOrderFilters();
      applyAgentFilters((e as CustomEvent<OrderFilterRequest>).detail);
    };
    window.addEventListener(ORDER_FILTERS_EVENT, handler);
    return () => window.removeEventListener(ORDER_FILTERS_EVENT, handler);
  }, []);

  // AccountingDataTable kolon tanımları
  const orderColumns = useMemo<DataTableColumn<OrderListItem>[]>(() => [
    {
      id: 'rowNumber',
      label: tt('Sıra', 'No'),
      width: '56px',
      align: 'center',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {index + 1}
        </Box>
      ),
    },
    {
      id: 'orderNumber',
      label: tt('Sipariş No', 'Order No'),
      field: 'orderNumber',
      width: '12%',
      sortable: true,
    },
    {
      id: 'type',
      label: tt('Tür', 'Type'),
      field: 'orderType',
      width: '8%',
      sortable: true,
      render: (row) => (
        row.source === 'b2b' ? (
          <Chip
            icon={<HubIcon sx={{ fontSize: 14 }} />}
            label="B2B"
            size="small"
            color="info"
            variant="filled"
            sx={{ minWidth: '72px', justifyContent: 'center' }}
          />
        ) : (
          <Chip
            label={row.type === 'sales' ? tt('Satış', 'Sales') : tt('Alış', 'Purchase')}
            size="small"
            color={row.type === 'sales' ? 'primary' : 'secondary'}
            variant="outlined"
            // Tür chip'leri yatayda eşit genişlikte ve ortalı görünür
            sx={{ minWidth: '72px', justifyContent: 'center' }}
          />
        )
      ),
    },
    {
      id: 'accountName',
      label: tt('Firma', 'Company'),
      field: 'accountName',
      width: '18%',
      sortable: true,
      tooltip: (row) => row.accountName,
      // Proje bağı cari adının altında küçük etiket olarak gösterilir (PYS)
      render: (row) => (
        <Box>
          <Typography variant="body2" noWrap>{row.accountName}</Typography>
          <ProjectBadge projectName={row.projectName} />
          {row.source === 'b2b' && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {tt('Şube:', 'Branch:')} {b2bBranchLabel(row.b2bBranchId)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'orderDate',
      label: tt('Tarih', 'Date'),
      field: 'orderDate',
      width: '9%',
      sortable: true,
      render: (row) => formatDate(row.orderDate),
    },
    {
      id: 'deliveryDate',
      label: tt('Teslimat', 'Delivery'),
      field: 'deliveryDate',
      width: '9%',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {formatDate(row.deliveryDate)}
          {row.isDeliveryOverdue && (
            <Tooltip title={tt('Teslimat süresi geçmiş!', 'Delivery is overdue!')}>
              <WarningAmberIcon color="error" sx={{ fontSize: 16 }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'grandTotal',
      label: tt('Tutar', 'Amount'),
      field: 'grandTotal',
      width: '12%',
      sortable: true,
      // Banka/kasa ile uyumlu Typography stiline geçirildi (variant="body2", bold, success renk, sağa hizalı)
      render: (row) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="success.main"
          sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          {row.grandTotal.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
          {row.currency === 'USD' ? '$' : row.currency === 'EUR' ? '€' : '₺'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      field: 'status',
      width: '12%',
      sortable: true,
      render: (row) => {
        // B2B satırları kendi durum etiketlerini kullanır (kargoda/teslim vb.)
        if (row.source === 'b2b') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {b2bStatusChip(row.status)}
              {row.b2bInvoiced && (
                <Chip size="small" color="success" icon={<ReceiptIcon />} label={tt('Faturalaştı', 'Invoiced')} variant="outlined" />
              )}
            </Box>
          );
        }
        const tooltipParts: string[] = [];
        if (row.sourceOfferNumber) tooltipParts.push(`${tt('Teklif', 'Offer')}: ${row.sourceOfferNumber}`);
        if (row.linkedWaybillNumber) tooltipParts.push(`${tt('İrsaliye', 'Waybill')}: ${row.linkedWaybillNumber}`);
        if (row.linkedInvoiceNumber) tooltipParts.push(`${tt('Fatura', 'Invoice')}: ${row.linkedInvoiceNumber}`);
        const tooltipText = tooltipParts.length > 0 ? tooltipParts.join(' | ') : undefined;
        
        return (
          <Chip
            label={getStatusText(row.status)}
            size="small"
            color={getStatusColor(row.status)}
            // Tüm durum chip'leri eşit genişlikte görünmesi için minWidth eklendi
            sx={{ cursor: 'default', minWidth: '160px', justifyContent: 'center' }}
            title={tooltipText}
          />
        );
      },
    },
    {
      id: 'processStock',
      label: tt('Stok', 'Stock'),
      field: 'processStock',
      width: '6%',
      align: 'center' as const,
      render: (row) => (
        // B2B satırında stok, faturalaştırma sırasında işlenir; burada göstermeyiz
        row.source === 'b2b' ? (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ) : (
          // Hayır yazanlar kırmızı (error), Evet yazanlar yeşil (success) gösterilir
          <Chip
            label={row.processStock ? tt('Evet', 'Yes') : tt('Hayır', 'No')}
            size="small"
            color={row.processStock ? 'success' : 'error'}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        )
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      width: '6%',
      align: 'center' as const,
      render: (row) => (
        row.source === 'b2b' ? (
          <Tooltip title={tt('Detay / Faturalaştır', 'Details / Invoice')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openB2bDetail(row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e, row.id, row.type);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )
      ),
    },
  ], [isTr, localeCode]);
  
  // Sipariş silme
  const handleDeleteOrder = async () => {
    const idToDelete = orderToDelete || selectedOrderId;
    if (!idToDelete || !companyId) {
      handleMenuClose();
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      setLoading(true);
      const deleteDto = {
        id: idToDelete,
        companyId: companyId,
        deletedBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      
      const response = await accountingApi.post(ACCOUNTING_API.ORDER_DELETE, deleteDto);
      
      const result = response.data;
      
      if (result.success) {
        console.log('✅ Sipariş başarıyla silindi');
        loadOrders();
      } else {
        throw new Error(result.message || tt('Sipariş silinemedi', 'Order could not be deleted'));
      }
      
      handleMenuClose();
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    } catch (err: any) {
      console.error('❌ Sipariş silme hatası:', err);
      setError(err.message || tt('Sipariş silinirken bir hata oluştu.', 'An error occurred while deleting order.'));
    } finally {
      setLoading(false);
    }
  };

  // Siparişi faturaya dönüştürme - localStorage + navigate pattern
  const handleConvertToInvoice = async () => {
    const idToConvert = orderToConvert || selectedOrderId;
    if (!idToConvert || !companyId) {
      handleMenuClose();
      setConvertDialogOpen(false);
      return;
    }
    
    // Sipariş tipini bul
    const orderItem = orders.find(o => o.id === idToConvert);
    const orderType = orderItem?.type || selectedOrderType;
    
    try {
      setConverting(true);
      
      // API'den detaylı sipariş bilgisini çek
      console.log('📤 [API] Sipariş detayları çekiliyor (fatura için):', idToConvert);
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(idToConvert));
      
      const result = response.data;
      if (!result.success || !result.data) {
        throw new Error(result.message || tt('Sipariş detayları yüklenemedi', 'Order details could not be loaded'));
      }
      
      const orderData = result.data;
      
      const orderDataForInvoice = {
        orderId: orderData.id,
        orderNumber: orderData.orderNumber,
        accountId: orderData.accountId?.toString() || '',
        account: {
          companyName: orderData.accountName || '',
          taxNumber: orderData.accountTaxNumber || '',
          taxOffice: orderData.accountTaxOffice || '',
          address: orderData.accountAddress || '',
          phone: orderData.accountPhone || '',
          email: orderData.accountEmail || '',
        },
        items: orderData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.itemId || item.id,
          stockId: item.itemId || item.id,
          name: item.itemName,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          enteredPrice: item.enteredPrice || item.unitPrice,
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          netTotal: item.netTotal || 0,
          vatTotal: item.vatTotal || item.vatAmount || 0,
          vatAmount: item.vatAmount || item.vatTotal || 0,
          grossTotal: item.grossTotal || 0,
          warehouseId: item.warehouseId || '',
        })) || [],
        notes: orderData.notes ? `${tt('Sipariş No', 'Order No')}: ${orderData.orderNumber} - ${orderData.notes}` : `${tt('Sipariş No', 'Order No')}: ${orderData.orderNumber}`,
        currency: orderData.currency || 'TRY',
      };
      
      console.log('💾 localStorage\'a kaydedilen veri (fatura):', orderDataForInvoice);
      localStorage.setItem('orderToInvoice', JSON.stringify(orderDataForInvoice));
      
      setConvertDialogOpen(false);
      setOrderToConvert(null);
      
      if (orderType === 'sales') {
        navigate('/fatura/satis/yeni?fromOrder=true');
      } else {
        navigate('/fatura/alis/yeni?fromOrder=true');
      }
    } catch (err: any) {
      console.error('❌ Sipariş detayları yüklenirken hata:', err);
      setError(err.message || tt('Sipariş detayları yüklenirken bir hata oluştu.', 'An error occurred while loading order details.'));
    } finally {
      setConverting(false);
    }
  };

  // Siparişi irsaliyeye dönüştürme - localStorage + navigate pattern
  const handleConvertToWaybill = async () => {
    if (!selectedOrderId || !companyId) {
      handleMenuClose();
      return;
    }
    
    const currentOrderId = selectedOrderId;
    handleMenuClose();
    
    try {
      setLoading(true);
      
      console.log('📤 [API] Sipariş detayları çekiliyor (irsaliye için):', currentOrderId);
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(currentOrderId));
      
      const result = response.data;
      if (!result.success || !result.data) {
        throw new Error(result.message || tt('Sipariş detayları yüklenemedi', 'Order details could not be loaded'));
      }
      
      const orderData = result.data;
      
      const orderDataForWaybill = {
        orderId: orderData.id,
        orderNumber: orderData.orderNumber,
        accountId: orderData.accountId?.toString() || '',
        account: {
          companyName: orderData.accountName || '',
          taxNumber: orderData.accountTaxNumber || '',
          taxOffice: orderData.accountTaxOffice || '',
          address: orderData.accountAddress || '',
          phone: orderData.accountPhone || '',
          email: orderData.accountEmail || '',
        },
        items: orderData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.itemId || item.id,
          stockId: item.itemId || item.id,
          name: item.itemName,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          enteredPrice: item.enteredPrice || item.unitPrice,
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          netTotal: item.netTotal || 0,
          vatTotal: item.vatTotal || item.vatAmount || 0,
          vatAmount: item.vatAmount || item.vatTotal || 0,
          grossTotal: item.grossTotal || 0,
          warehouseId: item.warehouseId || '',
        })) || [],
        notes: orderData.notes ? `${tt('Sipariş No', 'Order No')}: ${orderData.orderNumber} - ${orderData.notes}` : `${tt('Sipariş No', 'Order No')}: ${orderData.orderNumber}`,
        currency: orderData.currency || 'TRY',
      };
      
      console.log('💾 localStorage\'a kaydedilen veri (irsaliye):', orderDataForWaybill);
      localStorage.setItem('orderToWaybill', JSON.stringify(orderDataForWaybill));
      
      navigate('/irsaliye/yeni?fromOrder=true');
    } catch (err: any) {
      console.error('❌ Sipariş detayları yüklenirken hata:', err);
      setError(err.message || tt('Sipariş detayları yüklenirken bir hata oluştu.', 'An error occurred while loading order details.'));
    } finally {
      setLoading(false);
    }
  };

  // Sipariş görüntüleme handler'ı
  const handleViewOrder = async (orderId: string, type: 'sales' | 'purchase') => {
    if (!orderId || !companyId) return;
    
    setLoadingOrderDisplay(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(orderId));
      
      const result = response.data;
      
      if (!result.success || !result.data) {
        throw new Error(result.message || tt('Sipariş detayları yüklenemedi', 'Order details could not be loaded'));
      }
      
      const orderData = result.data;
      
      const formattedOrder: OrderData = {
        id: orderData.id,
        orderNumber: orderData.orderNumber,
        orderType: orderData.orderType || (type === 'sales' ? 'SALES_ORDER' : 'PURCHASE_ORDER'),
        account: {
          companyName: orderData.accountName || '',
          taxNumber: orderData.accountTaxNumber || '',
          taxOffice: orderData.accountTaxOffice || '',
          address: orderData.accountAddress || '',
          phone: orderData.accountPhone || '',
          email: orderData.accountEmail || '',
        },
        orderDate: orderData.orderDate || orderData.date,
        deliveryDate: orderData.deliveryDate,
        status: orderData.status || '',
        processStock: orderData.processStock || false,
        subtotal: orderData.subtotal || 0,
        vatTotal: orderData.vatTotal || 0,
        sctTotal: orderData.sctTotal || 0,
        discountTotal: orderData.discountTotal || 0,
        grandTotal: orderData.grandTotal || 0,
        currency: orderData.currency || 'TRY',
        notes: orderData.notes,
        description: orderData.description,
        paymentMethod: orderData.paymentMethod,
        iban: orderData.iban,
        paymentNote: orderData.paymentNote,
        sourceOfferNumber: orderData.sourceOfferNumber,
        linkedInvoiceNumber: orderData.linkedInvoiceNumber || orderData.convertedInvoiceNumber,
        linkedWaybillNumber: orderData.linkedWaybillNumber || orderData.convertedWaybillNumber,
        createdAt: orderData.createdAt,
        updatedAt: orderData.updatedAt,
        items: orderData.items?.map((item: any) => ({
          itemName: item.itemName || item.name,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          netTotal: item.netTotal || (item.quantity * item.unitPrice),
          grossTotal: item.grossTotal || 0,
          lineNumber: item.lineNumber || 0,
        })) || [],
      };
      
      setSelectedOrderForDisplay(formattedOrder);
      setIsOrderDisplayModalOpen(true);
    } catch (err: any) {
      console.error('❌ Sipariş detayları yüklenirken hata:', err);
      setError(err.message || tt('Sipariş detayları yüklenirken bir hata oluştu.', 'An error occurred while loading order details.'));
    } finally {
      setLoadingOrderDisplay(false);
    }
  };

  // Düzenleme
  const handleEditOrder = (orderId: string, type: 'sales' | 'purchase') => {
    if (type === 'sales') {
      navigate(`/siparis/satis/duzenle/${orderId}`);
    } else {
      navigate(`/siparis/alis/duzenle/${orderId}`);
    }
  };

  // Excel'e aktarma
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      const workbook = new ExcelJS.Workbook();
      
      // Satış Siparişleri sayfası
      const salesSheet = workbook.addWorksheet(tt('Satış Siparişleri', 'Sales Orders'));
      salesSheet.mergeCells('A1:H1');
      const salesTitleCell = salesSheet.getCell('A1');
      salesTitleCell.value = tt('Satış Siparişleri Listesi', 'Sales Orders List');
      salesTitleCell.style = { font: { bold: true, size: 14 }, alignment: { horizontal: 'center' } };
      salesSheet.addRow([]);
      
      const salesHeaderRow = salesSheet.addRow([
        tt('Sipariş No', 'Order No'),
        tt('Müşteri', 'Customer'),
        tt('Tarih', 'Date'),
        tt('Teslimat Tarihi', 'Delivery Date'),
        tt('Para Birimi', 'Currency'),
        tt('Tutar', 'Amount'),
        tt('Durum', 'Status'),
        tt('Stok İşlenmiş', 'Stock Processed')
      ]);
      salesHeaderRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        };
      });
      
      orders.filter(o => o.type === 'sales').forEach((order) => {
        salesSheet.addRow([
          order.orderNumber, order.accountName, formatDate(order.orderDate),
          formatDate(order.deliveryDate), order.currency, order.grandTotal,
          getStatusText(order.status), order.processStock ? tt('Evet', 'Yes') : tt('Hayır', 'No')
        ]);
      });
      
      [15, 30, 15, 15, 10, 15, 15, 12].forEach((w, i) => { salesSheet.getColumn(i + 1).width = w; });
      salesSheet.getColumn(6).numFmt = '#,##0.00';
      
      // Alış Siparişleri sayfası
      const purchaseSheet = workbook.addWorksheet(tt('Alış Siparişleri', 'Purchase Orders'));
      purchaseSheet.mergeCells('A1:H1');
      const purchTitleCell = purchaseSheet.getCell('A1');
      purchTitleCell.value = tt('Alış Siparişleri Listesi', 'Purchase Orders List');
      purchTitleCell.style = { font: { bold: true, size: 14 }, alignment: { horizontal: 'center' } };
      purchaseSheet.addRow([]);
      
      const purchHeaderRow = purchaseSheet.addRow([
        tt('Sipariş No', 'Order No'),
        tt('Tedarikçi', 'Supplier'),
        tt('Tarih', 'Date'),
        tt('Teslimat Tarihi', 'Delivery Date'),
        tt('Para Birimi', 'Currency'),
        tt('Tutar', 'Amount'),
        tt('Durum', 'Status'),
        tt('Stok İşlenmiş', 'Stock Processed')
      ]);
      purchHeaderRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        };
      });
      
      orders.filter(o => o.type === 'purchase').forEach((order) => {
        purchaseSheet.addRow([
          order.orderNumber, order.accountName, formatDate(order.orderDate),
          formatDate(order.deliveryDate), order.currency, order.grandTotal,
          getStatusText(order.status), order.processStock ? tt('Evet', 'Yes') : tt('Hayır', 'No')
        ]);
      });
      
      [15, 30, 15, 15, 10, 15, 15, 12].forEach((w, i) => { purchaseSheet.getColumn(i + 1).width = w; });
      purchaseSheet.getColumn(6).numFmt = '#,##0.00';
      
      // Zebrastripe
      [salesSheet, purchaseSheet].forEach(sheet => {
        for (let i = 4; i <= sheet.rowCount; i++) {
          const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          sheet.getRow(i).eachCell(cell => {
            cell.style = {
              ...cell.style,
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: { top: { style: 'thin' as const }, left: { style: 'thin' as const }, bottom: { style: 'thin' as const }, right: { style: 'thin' as const } }
            };
          });
        }
      });
      
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${tt('Siparisler_Listesi', 'Orders_List')}_${currentDate}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <ShoppingCartIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {tt('Siparişler', 'Orders')}
          </Typography>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && orders.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<PendingIcon />}
            color={STATS_COLORS.blue}
            title={`${orderStats.pendingCount} ${tt('Sipariş', 'Order')}`}
            subtitle={`${tt('Bekleyen', 'Pending')} (${formatStatsCurrency(orderStats.pendingTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CheckCircleIcon />}
            color={STATS_COLORS.green}
            title={`${orderStats.confirmedCount} ${tt('Sipariş', 'Order')}`}
            subtitle={`${tt('Onaylı / Sevkiyatta', 'Confirmed / In Shipment')} (${formatStatsCurrency(orderStats.confirmedTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<WarningAmberIcon />}
            color={STATS_COLORS.red}
            title={`${orderStats.overdueCount} ${tt('Sipariş', 'Order')}`}
            subtitle={tt('Teslimat Süresi Geçmiş', 'Delivery Overdue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={orderStats.conversionRate >= 50 ? STATS_COLORS.green : STATS_COLORS.orange}
            title={`%${orderStats.conversionRate}`}
            subtitle={tt('Siparişten Faturaya Dönüş Oranı', 'Order to Invoice Conversion Rate')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Sipariş Ara...', 'Search Order...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Sipariş Türü Filtresi */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('Sipariş Türü', 'Order Type')}</InputLabel>
              <Select
                value={orderTypeFilter}
                label={tt('Sipariş Türü', 'Order Type')}
                onChange={(e) => setOrderTypeFilter(e.target.value as 'all' | 'sales' | 'purchase' | 'b2b')}
              >
                <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                <SelectMenuItem value="sales">{tt('Satış Siparişleri', 'Sales Orders')}</SelectMenuItem>
                <SelectMenuItem value="purchase">{tt('Alış Siparişleri', 'Purchase Orders')}</SelectMenuItem>
                <SelectMenuItem value="b2b">{tt('B2B Siparişleri', 'B2B Orders')}</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Filtreleri Göster İkonu */}
          <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Tooltip title={showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}>
              <IconButton
                color={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Desktop Butonlar */}
          <Grid item xs={12} md={7} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/siparis/satis/yeni')}
              >
                {tt('Satış Siparişi', 'Sales Order')}
              </Button>
              <Button 
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/siparis/alis/yeni')}
              >
                {tt('Alış Siparişi', 'Purchase Order')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenFeaturesMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button fullWidth size="small" variant="contained" color="primary" startIcon={<AddIcon />}
                  onClick={() => navigate('/siparis/satis/yeni')}>
                  {tt('Satış', 'Sales')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button fullWidth size="small" variant="contained" color="secondary" startIcon={<AddIcon />}
                  onClick={() => navigate('/siparis/alis/yeni')}>
                  {tt('Alış', 'Purchase')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button fullWidth size="small" variant="outlined" onClick={handleOpenFeaturesMenu}
                  sx={{ minWidth: 'auto' }}>
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Filtreler */}
      {showFilters && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Durum', 'Status')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={tt('Durum', 'Status')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <SelectMenuItem value="all">{tt('Tüm Durumlar', 'All Statuses')}</SelectMenuItem>
                  <SelectMenuItem value="draft">{tt('Taslak', 'Draft')}</SelectMenuItem>
                  <SelectMenuItem value="pending">{tt('Beklemede', 'Pending')}</SelectMenuItem>
                  <SelectMenuItem value="confirmed">{tt('Onaylandı', 'Confirmed')}</SelectMenuItem>
                  <SelectMenuItem value="shipped">{tt('Sevk Edildi', 'Shipped')}</SelectMenuItem>
                  <SelectMenuItem value="delivered">{tt('Teslim Edildi', 'Delivered')}</SelectMenuItem>
                  <SelectMenuItem value="canceled">{tt('İptal Edildi', 'Canceled')}</SelectMenuItem>
                  <SelectMenuItem value="invoiced">{tt('Faturalandırıldı', 'Invoiced')}</SelectMenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth variant="outlined" size="small"
                startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                onClick={() => handleSortChange('createdAt')}
              >
                {sortDirection === 'asc' ? tt('Eskiden Yeniye', 'Oldest to Newest') : tt('Yeniden Eskiye', 'Newest to Oldest')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          {/* Loading Overlay */}
          {loading && (
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10, borderRadius: 2,
            }}>
              <CircularProgress />
            </Box>
          )}
          
          {/* Desktop: AccountingDataTable */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<OrderListItem>
              columns={orderColumns}
              rows={displayRows}
              getRowId={(row) => `${row.source === 'b2b' ? 'b2b' : 'acc'}-${row.id}`}
              loading={loading}
              serverSidePagination={true}
              totalCount={displayTotalCount}
              page={page}
              pageSize={rowsPerPage}
              onPageChange={(newPage, newRowsPerPage) => {
                setPage(newPage);
                setRowsPerPage(newRowsPerPage);
              }}
              onSort={(field, direction) => handleSortChange(field)}
              sortField={sortColumn}
              sortDirection={sortDirection}
              emptyMessage={tt('Henüz sipariş kaydı bulunamadı.', 'No order records found yet.')}
              emptyIcon={<ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={tt('siparişler tablosu', 'orders table')}
              columnResizeStorageKey="orders"
            />
          </Box>

          {/* Mobile: Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {displayRows.length === 0 ? (
              <Box sx={{ 
                p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '200px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)', borderRadius: 2
              }}>
                <ShoppingCartIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {tt('Henüz sipariş kaydı bulunamadı.', 'No order records found yet.')}
                </Typography>
              </Box>
            ) : (
              displayRows.map((order, index) => {
                const isB2b = order.source === 'b2b';
                const rowNumber = page * rowsPerPage + index + 1;
                return (
                <Card 
                  key={`${isB2b ? 'b2b' : 'acc'}-${order.id}`}
                  sx={{ 
                    border: '1px solid',
                    borderColor: isB2b ? 'info.light' : order.type === 'sales' ? 'primary.light' : 'secondary.light',
                    bgcolor: isB2b ? 'rgba(2, 136, 209, 0.06)' : order.type === 'sales' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(156, 39, 176, 0.04)',
                    ...(order.isDeliveryOverdue ? { borderLeft: '4px solid', borderLeftColor: 'error.main' } : {})
                  }}
                >
                  <CardContent>
                    {/* Başlık */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`#${rowNumber}`}
                          size="small"
                          sx={{
                            minWidth: '40px',
                            fontWeight: 'bold',
                            bgcolor: 'primary.main',
                            color: 'white',
                          }}
                        />
                        {isB2b
                          ? <HubIcon color="info" sx={{ fontSize: 20 }} />
                          : <ShoppingCartIcon color={order.type === 'sales' ? 'primary' : 'secondary'} sx={{ fontSize: 20 }} />}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {order.orderNumber}
                        </Typography>
                        {order.isDeliveryOverdue && (
                          <Tooltip title={tt('Teslimat süresi geçmiş!', 'Delivery is overdue!')}>
                            <WarningAmberIcon color="error" sx={{ fontSize: 18 }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {isB2b ? (
                          <>
                            <Chip icon={<HubIcon sx={{ fontSize: 14 }} />} label="B2B" size="small" color="info" />
                            {b2bStatusChip(order.status)}
                          </>
                        ) : (
                          <>
                            <Chip 
                              label={order.type === 'sales' ? tt('Satış', 'Sales') : tt('Alış', 'Purchase')} 
                              size="small"
                              color={order.type === 'sales' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                            <Chip 
                              label={getStatusText(order.status)} 
                              size="small"
                              color={getStatusColor(order.status)}
                            />
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Firma */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {order.type === 'sales' ? tt('Müşteri:', 'Customer:') : tt('Tedarikçi:', 'Supplier:')}
                      </Typography>
                      <Typography variant="body2">{order.accountName}</Typography>
                      {isB2b && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {tt('Şube:', 'Branch:')} {b2bBranchLabel(order.b2bBranchId)}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Tarih ve Teslimat */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">{tt('Tarih:', 'Date:')}</Typography>
                        <Typography variant="body2">{formatDate(order.orderDate)}</Typography>
                      </Box>
                      {!isB2b && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">{tt('Teslimat:', 'Delivery:')}</Typography>
                          <Typography variant="body2" color={order.isDeliveryOverdue ? 'error.main' : 'inherit'}>
                            {formatDate(order.deliveryDate)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Tutar */}
                    <Box sx={{ 
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' 
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">{tt('Tutar:', 'Amount:')}</Typography>
                        {isB2b && order.b2bInvoiced && (
                          <Chip label={tt('Faturalaştı', 'Invoiced')} size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                        {!isB2b && order.processStock && (
                          <Chip label={tt('Stok İşlendi', 'Stock Processed')} size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" color={isB2b ? 'info.main' : order.type === 'sales' ? 'primary.main' : 'secondary.main'}>
                        {formatCurrency(order.grandTotal, order.currency)}
                      </Typography>
                    </Box>
                    
                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      {isB2b ? (
                        <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />} onClick={() => openB2bDetail(order.id)}>
                          {tt('Detay / Faturalaştır', 'Details / Invoice')}
                        </Button>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => handleEditOrder(order.id, order.type)} sx={{ bgcolor: 'action.hover' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, order.id, order.type)} sx={{ bgcolor: 'action.hover' }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
                );
              })
            )}
            
            {/* Mobile Pagination */}
            <TablePagination
              component="div"
              count={displayTotalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
              labelDisplayedRows={({ from, to, count }) => (isTr ? `${count} kayıttan ${from}-${to} arası` : `${from}-${to} of ${count}`)}
              sx={{
                '.MuiTablePagination-toolbar': { paddingLeft: 1, paddingRight: 1 },
                '.MuiTablePagination-selectLabel': { display: 'none' },
                '.MuiTablePagination-displayedRows': { fontSize: '0.875rem' }
              }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* 3 Nokta (Özellikler) Menüsü */}
      <Menu
        anchorEl={anchorElFeatures}
        open={Boolean(anchorElFeatures)}
        onClose={handleCloseFeaturesMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => { exportToExcel(); handleCloseFeaturesMenu(); }}
          disabled={loadingExport || loading || orders.length === 0}
        >
          {loadingExport ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />}
          {tt('Excel İndir', 'Download Excel')}
        </MenuItem>
      </Menu>
      
      {/* İşlemler Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          if (selectedOrderId) handleViewOrder(selectedOrderId, selectedOrderType);
          handleMenuClose();
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
          {tt('Görüntüle', 'View')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedOrderId) handleEditOrder(selectedOrderId, selectedOrderType);
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Düzenle', 'Edit')}
        </MenuItem>
        {/* Fatura: Göster veya Oluştur */}
        {(() => {
          const selected = orders.find(o => o.id === selectedOrderId);
          if (!selected) return null;
          // Fatura zaten oluşturulduysa → Göster
          console.log('🔍 [MENÜ] Seçili sipariş fatura durumu:', {
            orderId: selected.id,
            orderNumber: selected.orderNumber,
            convertedInvoiceId: selected.convertedInvoiceId,
            linkedInvoiceNumber: selected.linkedInvoiceNumber,
            status: selected.status,
            typeofConvertedInvoiceId: typeof selected.convertedInvoiceId,
          });
          if (selected.convertedInvoiceId) {
            const invoiceId = selected.convertedInvoiceId;
            return (
              <MenuItem onClick={async () => {
                handleMenuClose();
                if (!companyId || !invoiceId) return;
                try {
                  setLoading(true);
                  const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId));
                  const result = response.data;
                  if (result.success && result.data) {
                    const inv = result.data;
                    const formattedInvoice: InvoiceData = {
                      id: inv.id,
                      invoiceNumber: inv.invoiceNumber,
                      account: {
                        companyName: inv.accountName || '',
                        taxNumber: inv.accountTaxNumber || '',
                        taxOffice: inv.accountTaxOffice || '',
                        address: inv.accountAddress || '',
                        phone: inv.accountPhone || '',
                        email: inv.accountEmail || '',
                      },
                      date: inv.invoiceDate || inv.date,
                      dueDate: inv.dueDate,
                      items: inv.items?.map((item: any) => ({
                        itemName: item.itemName || item.name,
                        quantity: item.quantity,
                        unit: item.unit,
                        unitPrice: item.unitPrice,
                        vatRate: item.vatRate || 0,
                        sctRate: item.sctRate || 0,
                        discountRate: item.discountRate || 0,
                        lineTotal: item.netTotal || (item.quantity * item.unitPrice),
                        vatAmount: item.vatAmount || item.vatTotal || 0,
                        sctAmount: item.sctAmount || 0,
                        grossTotal: item.grossTotal || 0,
                      })) || [],
                      notes: inv.notes,
                      subtotal: inv.subtotal || 0,
                      vatTotal: inv.vatTotal || 0,
                      sctTotal: inv.sctTotal || 0,
                      discountTotal: inv.discountTotal || 0,
                      grandTotal: inv.grandTotal || 0,
                      currency: inv.currency || 'TRY',
                      type: selected.type === 'sales' ? 'sales' : 'purchase',
                      sourceOfferId: inv.sourceOfferId,
                      sourceOfferNumber: inv.sourceOfferNumber,
                    };
                    setSelectedInvoiceForDisplay(formattedInvoice);
                    setIsInvoiceDisplayModalOpen(true);
                  }
                } catch (err) {
                  console.error('Fatura detayı yüklenemedi:', err);
                } finally {
                  setLoading(false);
                }
              }}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                {tt('Fatura Göster', 'View Invoice')} {selected.linkedInvoiceNumber ? `(${selected.linkedInvoiceNumber})` : ''}
              </MenuItem>
            );
          }
          // Fatura oluşturulabilir mi?
          if (selected.status === 'confirmed' || selected.status === 'delivered') {
            return (
              <MenuItem onClick={() => {
                setOrderToConvert(selectedOrderId);
                setConvertDialogOpen(true);
                handleMenuClose();
              }}>
                <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                {tt('Fatura Oluştur', 'Create Invoice')}
              </MenuItem>
            );
          }
          return null;
        })()}
        {/* İrsaliye: Göster veya Oluştur */}
        {(() => {
          const selected = orders.find(o => o.id === selectedOrderId);
          if (!selected) return null;
          // İrsaliye zaten oluşturulduysa → Modal ile Göster
          if (selected.linkedWaybillId) {
            return (
              <MenuItem onClick={async () => {
                handleMenuClose();
                if (!companyId || !selected.linkedWaybillId) return;
                try {
                  setLoading(true);
                  const response = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(selected.linkedWaybillId));
                  const result = response.data;
                  if (result.success && result.data) {
                    const wd = result.data;
                    setSelectedWaybillForModal({
                      id: wd.id,
                      waybillNumber: wd.waybillNumber || '',
                      accountId: wd.accountId?.toString() || '',
                      account: {
                        companyName: wd.accountName || '',
                        taxNumber: wd.accountTaxNumber || '',
                        taxOffice: wd.accountTaxOffice || '',
                        address: wd.accountAddress || '',
                        phone: wd.accountPhone || '',
                        email: wd.accountEmail || '',
                      },
                      waybillDate: wd.waybillDate || '',
                      subtotal: wd.subtotal || 0,
                      vatTotal: wd.vatTotal || 0,
                      grandTotal: wd.grandTotal || 0,
                      currency: wd.currency || 'TRY',
                      showPrices: wd.showPrices ?? true,
                      notes: wd.notes || '',
                      createdAt: wd.createdAt || '',
                      updatedAt: wd.updatedAt || '',
                      items: wd.items?.map((item: any) => ({
                        id: item.id,
                        stockId: item.stockId,
                        itemName: item.itemName,
                        unit: item.unit,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice,
                        vatRate: item.vatRate,
                        netTotal: item.netTotal || 0,
                        grossTotal: item.grossTotal || 0,
                      })) || [],
                    });
                    setIsWaybillModalOpen(true);
                  }
                } catch (err) {
                  console.error('İrsaliye detayı yüklenemedi:', err);
                } finally {
                  setLoading(false);
                }
              }}>
                <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                {tt('İrsaliye Göster', 'View Waybill')} {selected.linkedWaybillNumber ? `(${selected.linkedWaybillNumber})` : ''}
              </MenuItem>
            );
          }
          // İrsaliye oluşturulabilir mi?
          if (selected.status === 'confirmed' || selected.status === 'shipped') {
            return (
              <MenuItem onClick={handleConvertToWaybill}>
                <LocalShippingIcon fontSize="small" sx={{ mr: 1 }} />
                {tt('İrsaliye Oluştur', 'Create Waybill')}
              </MenuItem>
            );
          }
          return null;
        })()}
        <MenuItem onClick={() => {
          setOrderToDelete(selectedOrderId);
          setDeleteDialogOpen(true);
          handleMenuClose();
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          <Typography color="error.main">{tt('Sil', 'Delete')}</Typography>
        </MenuItem>
      </Menu>
      
      {/* Silme Onay Dialogu */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setOrderToDelete(null); }}>
        <DialogTitle>{tt('Siparişi Sil', 'Delete Order')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt('Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.', 'Are you sure you want to delete this order? This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setOrderToDelete(null); }}>{tt('İptal', 'Cancel')}</Button>
          <Button onClick={handleDeleteOrder} color="error" variant="contained">{tt('Sil', 'Delete')}</Button>
        </DialogActions>
      </Dialog>
      
      {/* İrsaliye Görüntüleme Modalı */}
      {selectedWaybillForModal && (
        <WaybillDisplayModal
          open={isWaybillModalOpen}
          onClose={() => { setIsWaybillModalOpen(false); setSelectedWaybillForModal(null); }}
          waybill={selectedWaybillForModal}
          companyId={companyId}
        />
      )}
      
      {/* Fatura Görüntüleme Modalı */}
      <InvoiceDisplayModal
        open={isInvoiceDisplayModalOpen}
        onClose={() => {
          setIsInvoiceDisplayModalOpen(false);
          setSelectedInvoiceForDisplay(null);
        }}
        invoice={selectedInvoiceForDisplay}
        companyId={companyId}
      />
      
      {/* Sipariş Görüntüleme Modalı */}
      <OrderDisplayModal
        open={isOrderDisplayModalOpen}
        onClose={() => {
          setIsOrderDisplayModalOpen(false);
          setSelectedOrderForDisplay(null);
        }}
        order={selectedOrderForDisplay}
        companyId={companyId}
      />

      {/* B2B gelen sipariş detay / faturalaştırma dialogu */}
      <IncomingOrderDetailDialog
        orderId={b2bDetailOrderId}
        open={b2bDetailOpen}
        onClose={() => setB2bDetailOpen(false)}
        onChanged={loadB2bOrders}
        branchLabel={b2bBranchLabel}
      />
      
      {/* Faturaya Dönüştürme Dialogu */}
      <Dialog open={convertDialogOpen} onClose={() => { setConvertDialogOpen(false); setOrderToConvert(null); }}>
        <DialogTitle>{tt('Siparişi Faturaya Dönüştür', 'Convert Order to Invoice')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt('Bu sipariş faturaya dönüştürülecektir. Sipariş durumu "Faturalandırıldı" olarak güncellenecektir. Devam etmek istiyor musunuz?', 'This order will be converted to an invoice. Order status will be updated to "Invoiced". Do you want to continue?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConvertDialogOpen(false); setOrderToConvert(null); }} disabled={converting}>{tt('İptal', 'Cancel')}</Button>
          <Button onClick={handleConvertToInvoice} color="primary" variant="contained" disabled={converting}
            startIcon={converting ? <CircularProgress size={16} /> : <ReceiptIcon />}>
            {converting ? tt('Dönüştürülüyor...', 'Converting...') : tt('Faturaya Dönüştür', 'Convert to Invoice')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Orders;
